/**
 * 「这一轮到底降级了没有」—— 把同一个 `correlation_id` 的那几行 attempt 读成一句话。
 *
 * 这是 `90-known-issues.md` 里「fallback 不可观测」那条的解药：链上有几步、主力是谁、它为什么挂、
 * 最后是谁答的，全在 `llm_call_log` 里躺着，只是从来没人把它们串起来看过。
 *
 * 纯函数，不碰 DOM、不碰网络。
 */

/**
 * @typedef {import('../../api/llm/index.js').CallRow} CallRow
 * @typedef {object} ChainSummary
 * @property {'no_fallback'|'fell_back'|'all_failed'} outcome
 * @property {CallRow} primary               第 0 步，主力
 * @property {CallRow|null} answered         最后答上来的那一步；一步都没答上来就是 null
 * @property {number|null} answeredIndex     答上来的是第几步（`route.attempt_index`）
 * @property {string|null} primaryFailReason 主力挂了的原因；主力自己答了就是 null
 */

/**
 * @param {Array<CallRow & { is_current: boolean }>} attempts  按 attempt_index 升序（后端已排好）
 * @returns {ChainSummary}
 */
export function summarizeChain(attempts) {
  if (attempts.length === 0) throw new Error('summarizeChain: chain has no attempt')
  const primary = attempts[0]
  const answered = attempts.find((attempt) => attempt.status === 'ok') ?? null
  const primaryFailReason = primary.status === 'ok' ? null : primary.error_reason

  if (answered === null) return { outcome: 'all_failed', primary, answered, answeredIndex: null, primaryFailReason }
  const answeredIndex = stepIndex(answered)
  if (answered === primary) return { outcome: 'no_fallback', primary, answered, answeredIndex, primaryFailReason }
  return { outcome: 'fell_back', primary, answered, answeredIndex, primaryFailReason }
}

/**
 * 链上一步是第几步。链是按 `correlation_id` 拉出来的，链上的每一行按定义都走过路由链 ——
 * 出现别的 `route.state` 就是这一行是脏的，抛错，不猜一个序号。
 * @param {CallRow} attempt
 * @returns {number}
 */
export function stepIndex(attempt) {
  if (attempt.route.state !== 'routed') {
    throw new Error(`summarizeChain: 链上的 ${attempt.request_id} 路由归因是 ${attempt.route.state}，没有 attempt_index`)
  }
  return attempt.route.attempt_index
}

/**
 * 一步的显示名：`厂商/模型`。链上两步同厂不同模型是常态，所以两个都要出现。
 * @param {CallRow} attempt
 */
export function stepName(attempt) {
  return `${attempt.provider} / ${attempt.model}`
}
